/**
 * BinTransferDialog — scan-based rack-to-rack material transfer with
 * photo evidence + audit trail. Each transfer:
 *   1. Storekeeper selects source rack + destination rack
 *   2. Enters (or scans) unit IDs — one per line, unlimited
 *   3. Optionally attaches photo evidence (uploaded to Object Storage)
 *   4. Posts to `POST /mtx/material-units/transfer` — backend appends
 *      to `mtx_scan_events` (hash-chained) and updates each unit's
 *      `warehouseLocation` + `currentBin` atomically.
 */
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, ArrowRightLeft, Loader2, Camera, Trash2, Check,
         Package, MapPin, ScanLine, AlertTriangle } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

export default function BinTransferDialog({ projectId, defaultFromLocation, onClose, onDone }) {
  const [racks, setRacks] = useState([]);
  const [fromLoc, setFromLoc] = useState(defaultFromLocation || '');
  const [toLoc, setToLoc] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [scannedUnits, setScannedUnits] = useState([]);   // [{id, materialDescription, currentBin}]
  const [photos, setPhotos] = useState([]);   // [{url, path}]
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const scanRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/warehouses`);
        setRacks((data.warehouses || []).filter(w => w.type === 'RACK'));
      } catch { setRacks([]); }
    })();
    setTimeout(() => scanRef.current?.focus(), 300);
  }, [projectId]);

  const resolveUnit = async (code) => {
    if (!code?.trim()) return;
    if (scannedUnits.find(u => u.id === code.trim() || u.materialUnitId === code.trim())) {
      toast.info('Unit already added');
      return;
    }
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/mtx/material-units/lookup?q=${encodeURIComponent(code.trim())}`);
      if (data.match) {
        setScannedUnits(prev => [...prev, {
          id: data.match.id,
          materialDescription: data.match.materialDescription,
          materialCode: data.match.materialCode,
          currentBin: data.match.currentBin,
          currentState: data.match.currentState,
        }]);
        setScanInput('');
        // Auto-suggest source rack from first scanned unit
        if (!fromLoc && data.match.currentBin) setFromLoc(data.match.currentBin);
      } else {
        toast.error(`No unit matches "${code}"`);
      }
    } catch { toast.error('Lookup failed'); }
  };

  const handleScanKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); resolveUnit(scanInput); }
  };

  const removeUnit = (id) => {
    setScannedUnits(prev => prev.filter(u => u.id !== id));
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'transfer-evidence');
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mtx/uploads/photo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos(prev => [...prev, { url: data.url, path: data.path, size: data.size }]);
      toast.success('Photo attached');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo upload failed');
    } finally { setUploadingPhoto(false); }
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!fromLoc || !toLoc) return toast.error('Select both source and destination racks');
    if (fromLoc === toLoc) return toast.error('Source and destination must differ');
    if (scannedUnits.length === 0) return toast.error('Scan at least one unit');
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mtx/material-units/transfer`,
        { fromLocation: fromLoc, toLocation: toLoc,
          materialUnitIds: scannedUnits.map(u => u.id),
          photoUrls: photos.map(p => p.url),
          notes });
      toast.success(`Transferred ${data.transferred} unit(s) — audit #${(data.auditId || '').slice(-6)}`);
      onDone?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    } finally { setBusy(false); }
  };

  const fromRack = racks.find(r => r.location === fromLoc);
  const toRack   = racks.find(r => r.location === toLoc);

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-end"
      style={{ background: 'rgba(15,23,42,0.45)' }} onClick={onClose}>
      <div className="w-[520px] bg-white shadow-2xl overflow-y-auto p-4"
        onClick={e => e.stopPropagation()} data-testid="bin-transfer-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>Rack Transfer</div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>Scan units + move to another rack — audit-logged</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="bin-transfer-close"><X className="w-4 h-4" /></button>
        </div>

        {/* From / To selectors */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>From Rack</label>
            <select value={fromLoc} onChange={e => setFromLoc(e.target.value)}
              data-testid="bin-transfer-from"
              className="w-full px-2 py-1.5 rounded border text-[11px]"
              style={{ borderColor: C.border, background: '#fff' }}>
              <option value="">Select source…</option>
              {racks.map(r => (
                <option key={r.id} value={r.location}>
                  {r.parentBinCode} · {r.disciplineCode} · {r.discipline} ({r.balanceStock || 0})
                </option>
              ))}
            </select>
            {fromRack && (
              <div className="text-[9px] mt-1 flex items-center gap-1" style={{ color: fromRack.disciplineColor }}>
                <MapPin className="w-2.5 h-2.5" /> Stock available: <b>{fromRack.balanceStock || 0}</b>
              </div>
            )}
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>To Rack</label>
            <select value={toLoc} onChange={e => setToLoc(e.target.value)}
              data-testid="bin-transfer-to"
              className="w-full px-2 py-1.5 rounded border text-[11px]"
              style={{ borderColor: C.border, background: '#fff' }}>
              <option value="">Select destination…</option>
              {racks.filter(r => r.location !== fromLoc).map(r => (
                <option key={r.id} value={r.location}>
                  {r.parentBinCode} · {r.disciplineCode} · {r.discipline} ({r.fillPct ?? 0}% full)
                </option>
              ))}
            </select>
            {toRack && (
              <div className="text-[9px] mt-1 flex items-center gap-1" style={{ color: toRack.disciplineColor }}>
                <MapPin className="w-2.5 h-2.5" /> Current fill: <b>{toRack.fillPct ?? 0}%</b>
                {toRack.fillStatus === 'DANGER' && <span className="ml-1 px-1 rounded text-[8.5px] text-white" style={{ background: C.danger }}>OVERLOADED</span>}
                {toRack.fillStatus === 'WARN' && <span className="ml-1 px-1 rounded text-[8.5px] text-white" style={{ background: C.warn }}>NEAR FULL</span>}
              </div>
            )}
          </div>
        </div>

        {/* Scan input */}
        <div className="mb-3 p-2 rounded-lg" style={{ background: '#F8FAFC', border: `1px solid ${C.border}` }}>
          <label className="text-[9px] uppercase font-semibold flex items-center gap-1" style={{ color: C.slateMuted }}>
            <ScanLine className="w-3 h-3" /> Scan or enter Unit / QR / RFID
          </label>
          <div className="flex gap-2 mt-1">
            <input ref={scanRef} value={scanInput} onChange={e => setScanInput(e.target.value)}
              onKeyDown={handleScanKey}
              data-testid="bin-transfer-scan-input"
              placeholder="Paste code + Enter (or use scanner)"
              className="flex-1 px-2 py-1 rounded border text-[11px] font-mono"
              style={{ borderColor: C.border, background: '#fff' }} />
            <Button size="sm" onClick={() => resolveUnit(scanInput)}
              data-testid="bin-transfer-add-unit"
              style={{ background: C.primary, color: '#fff' }}>Add</Button>
          </div>
          <div className="text-[9px] mt-1" style={{ color: C.slateMuted }}>
            Units scanned: <b>{scannedUnits.length}</b>
          </div>
        </div>

        {/* Scanned units list */}
        {scannedUnits.length > 0 && (
          <div className="mb-3 rounded-lg overflow-hidden border" style={{ borderColor: C.border, maxHeight: 200 }}>
            <div className="max-h-[200px] overflow-y-auto">
              {scannedUnits.map(u => (
                <div key={u.id} className="p-1.5 flex items-center gap-2 border-b text-[10px]"
                  style={{ borderColor: C.border }} data-testid={`bin-transfer-unit-${u.id}`}>
                  <Package className="w-3 h-3 flex-shrink-0" style={{ color: C.success }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold truncate" style={{ color: C.primary }}>{u.id}</div>
                    <div className="truncate" style={{ color: C.slateMuted }}>
                      {u.materialCode} · {u.materialDescription}
                    </div>
                    {u.currentBin && u.currentBin !== fromLoc && (
                      <div className="text-[9px] flex items-center gap-1" style={{ color: C.warn }}>
                        <AlertTriangle className="w-2.5 h-2.5" /> currently on: {u.currentBin}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeUnit(u.id)}
                    className="text-[9px] px-1 py-0.5 rounded"
                    style={{ background: '#FEE2E2', color: C.danger }}
                    data-testid={`bin-transfer-remove-${u.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        <div className="mb-3">
          <label className="text-[9px] uppercase font-semibold flex items-center gap-1 mb-1" style={{ color: C.slateMuted }}>
            <Camera className="w-3 h-3" /> Photo evidence (optional)
          </label>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative w-14 h-14 rounded overflow-hidden border" style={{ borderColor: C.border }}>
                <img src={p.url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(i)}
                  className="absolute top-0 right-0 bg-white rounded-bl p-0.5"
                  data-testid={`bin-transfer-photo-remove-${i}`}>
                  <X className="w-2.5 h-2.5" style={{ color: C.danger }} />
                </button>
              </div>
            ))}
            <button onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="bin-transfer-photo-add"
              className="w-14 h-14 rounded border-dashed border-2 flex items-center justify-center"
              style={{ borderColor: C.border, color: C.slateMuted }}>
              {uploadingPhoto
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Camera className="w-4 h-4" />}
            </button>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
              className="hidden"
              onChange={e => uploadPhoto(e.target.files?.[0])} />
          </div>
        </div>

        {/* Notes */}
        <div className="mb-3">
          <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Notes / reason (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            rows={2} placeholder="e.g. Consolidation — matching PO-ADR-00078 stock into single rack"
            data-testid="bin-transfer-notes"
            className="w-full px-2 py-1 rounded border text-[11px]"
            style={{ borderColor: C.border, background: '#fff' }} />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: C.border }}>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="bin-transfer-cancel">Cancel</Button>
          <Button size="sm" onClick={submit}
            disabled={busy || !fromLoc || !toLoc || scannedUnits.length === 0}
            data-testid="bin-transfer-submit"
            style={{ background: C.accent, color: '#fff' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <><Check className="w-3.5 h-3.5 mr-1" /> Transfer {scannedUnits.length} unit{scannedUnits.length===1?'':'s'}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
